"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { useMatch } from "fansunited-data-layer/client";
import { SectionHeader, EntityDisplay } from "fansunited-sports-ui";
import type { FUSportsFootballEventType } from "fansunited-data-layer";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { getEffectiveMinute, KEY_EVENT_TYPES } from "./matchEventHelpers";
import { TimelineEvent } from "./TimelineEvent";
import { HalfTimeDivider } from "./HalfTimeDivider";
interface MatchEventsProps {
  homeTeamName: string;
  awayTeamName: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  homeTeamCountryCode?: string;
  homeTeamCountryFlag?: string;
  homeTeamPrimaryColor?: string;
  homeTeamSecondaryColor?: string;
  awayTeamCountryCode?: string;
  awayTeamCountryFlag?: string;
  awayTeamPrimaryColor?: string;
  awayTeamSecondaryColor?: string;
}

/**
 * MatchEvents - Displays match events as a vertical timeline with cards
 */
export const MatchEvents: React.FC<MatchEventsProps> = t0 => {
  const $ = _c(58);
  const {
    homeTeamName,
    awayTeamName,
    homeTeamLogo,
    awayTeamLogo,
    homeTeamCountryCode,
    homeTeamCountryFlag,
    homeTeamPrimaryColor,
    homeTeamSecondaryColor,
    awayTeamCountryCode,
    awayTeamCountryFlag,
    awayTeamPrimaryColor,
    awayTeamSecondaryColor
  } = t0;
  const {
    events
  } = useMatch();
  const {
    allowPlayerPhotos
  } = useTenantSettings();
  const labels = useLabels();
  const eventLabels = labels.matchPage.events;
  let t1;
  if ($[0] !== events) {
    bb0: {
      if (!events || events.length === 0) {
        let t2;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = {
            sortedEvents: [],
            halfTimeIndex: -1,
            showHalfTimeDivider: false
          };
          $[2] = t2;
        } else {
          t2 = $[2];
        }
        t1 = t2;
        break bb0;
      }
      const keyEvents = events.filter(_temp);
      const sorted = [...keyEvents].sort(_temp2);
      if (sorted.length === 0) {
        let t2;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = {
            sortedEvents: [],
            halfTimeIndex: -1,
            showHalfTimeDivider: false
          };
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        t1 = t2;
        break bb0;
      }
      const hasHalftimeEvent = events.some(_temp3);
      const htIndex = sorted.findIndex((event, index) => {
        if (index === 0) {
          return false;
        }
        const prevEvent = sorted[index - 1];
        const prevMinute = getEffectiveMinute(prevEvent);
        const currentMinute = getEffectiveMinute(event);
        return prevMinute > 45 && currentMinute <= 45;
      });
      const shouldShowHT = hasHalftimeEvent || htIndex > 0 && sorted.length > 1;
      let t2;
      if ($[4] !== htIndex || $[5] !== shouldShowHT || $[6] !== sorted) {
        t2 = {
          sortedEvents: sorted,
          halfTimeIndex: htIndex,
          showHalfTimeDivider: shouldShowHT
        };
        $[4] = htIndex;
        $[5] = shouldShowHT;
        $[6] = sorted;
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      t1 = t2;
    }
    $[0] = events;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    sortedEvents,
    halfTimeIndex,
    showHalfTimeDivider
  } = t1;
  if (sortedEvents.length === 0) {
    return null;
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      typography: {
        name: "text-sm uppercase font-semibold text-foreground"
      }
    };
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== eventLabels.title) {
    t3 = {
      name: eventLabels.title,
      styles: t2
    };
    $[9] = eventLabels.title;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      container: "mb-2"
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t3) {
    t5 = <SectionHeader entity={t3} styles={t4} />;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== homeTeamCountryCode || $[15] !== homeTeamCountryFlag || $[16] !== homeTeamLogo || $[17] !== homeTeamPrimaryColor || $[18] !== homeTeamSecondaryColor) {
    t6 = <TeamLogo src={homeTeamLogo} size="md" countryCode={homeTeamCountryCode} countryFlag={homeTeamCountryFlag} primaryColor={homeTeamPrimaryColor} secondaryColor={homeTeamSecondaryColor} />;
    $[14] = homeTeamCountryCode;
    $[15] = homeTeamCountryFlag;
    $[16] = homeTeamLogo;
    $[17] = homeTeamPrimaryColor;
    $[18] = homeTeamSecondaryColor;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      imageSize: "md",
      textAlign: "right",
      typography: {
        name: "text-sm font-semibold text-foreground"
      }
    };
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== homeTeamName || $[22] !== t6) {
    t8 = <div className="flex-1 flex justify-end pr-4"><EntityDisplay name={homeTeamName} image={t6} imagePosition="right" styles={t7} /></div>;
    $[21] = homeTeamName;
    $[22] = t6;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="w-10 flex justify-center"><div className="w-2 h-2 rounded-full bg-muted-foreground/30" /></div>;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== awayTeamCountryCode || $[26] !== awayTeamCountryFlag || $[27] !== awayTeamLogo || $[28] !== awayTeamPrimaryColor || $[29] !== awayTeamSecondaryColor) {
    t10 = <TeamLogo src={awayTeamLogo} size="md" countryCode={awayTeamCountryCode} countryFlag={awayTeamCountryFlag} primaryColor={awayTeamPrimaryColor} secondaryColor={awayTeamSecondaryColor} />;
    $[25] = awayTeamCountryCode;
    $[26] = awayTeamCountryFlag;
    $[27] = awayTeamLogo;
    $[28] = awayTeamPrimaryColor;
    $[29] = awayTeamSecondaryColor;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      imageSize: "md",
      textAlign: "left",
      typography: {
        name: "text-sm font-semibold text-foreground"
      }
    };
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== awayTeamName || $[33] !== t10) {
    t12 = <div className="flex-1 flex justify-start pl-4"><EntityDisplay name={awayTeamName} image={t10} imagePosition="left" styles={t11} /></div>;
    $[32] = awayTeamName;
    $[33] = t10;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t12 || $[36] !== t8) {
    t13 = <div className="flex items-center mb-6">{t8}{t9}{t12}</div>;
    $[35] = t12;
    $[36] = t8;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-border -translate-x-1/2" />;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== allowPlayerPhotos || $[40] !== eventLabels || $[41] !== halfTimeIndex || $[42] !== showHalfTimeDivider || $[43] !== sortedEvents) {
    let t16;
    if ($[45] !== allowPlayerPhotos || $[46] !== eventLabels || $[47] !== halfTimeIndex || $[48] !== showHalfTimeDivider) {
      t16 = (event_0, index_0) => <React.Fragment key={event_0.id}><TimelineEvent event={event_0} isHome={event_0.competitorPosition === "ONE"} showPhotos={allowPlayerPhotos} eventLabels={eventLabels} />{showHalfTimeDivider && index_0 === halfTimeIndex - 1 && <HalfTimeDivider label={eventLabels.halfTime} />}</React.Fragment>;
      $[45] = allowPlayerPhotos;
      $[46] = eventLabels;
      $[47] = halfTimeIndex;
      $[48] = showHalfTimeDivider;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    t15 = sortedEvents.map(t16);
    $[39] = allowPlayerPhotos;
    $[40] = eventLabels;
    $[41] = halfTimeIndex;
    $[42] = showHalfTimeDivider;
    $[43] = sortedEvents;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[50] !== t15) {
    t16 = <div className="relative">{t14}<div className="relative flex flex-col gap-4">{t15}</div></div>;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== t13 || $[53] !== t16) {
    t17 = <div className="bg-card rounded-lg shadow-md overflow-hidden border border-border p-4">{t13}{t16}</div>;
    $[52] = t13;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== t17 || $[56] !== t5) {
    t18 = <div>{t5}{t17}</div>;
    $[55] = t17;
    $[56] = t5;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  return t18;
};
function _temp(e) {
  return KEY_EVENT_TYPES.includes(e.type as FUSportsFootballEventType);
}
function _temp2(a, b) {
  return getEffectiveMinute(b) - getEffectiveMinute(a);
}
function _temp3(e_0) {
  return e_0.type === "HALFTIME";
}